import { DndContext, DragOverlay } from '@dnd-kit/core';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type Lead, type LeadStatus } from '@up2stay/up2promo-api-client';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';

import { invalidateStatusBoardQueries } from '@/core/components/dashboard/dashboard-refresh-keys';
import { KanbanMinimap } from '@/core/components/leads/kanban-minimap';
import { LeadBoardCardContent } from '@/core/components/leads/lead-board-card-content';
import type { LeadCardMetaLines } from '@/core/components/leads/lead-card-content';
import type { LeadCardLabelMaps } from '@/core/components/leads/lead-card-labels';
import { kanbanBoardCollisionDetection } from '@/core/components/leads/leads-board-collision';
import { LeadsBoardColumn } from '@/core/components/leads/leads-board-column';
import {
  type LeadBoardItem,
  leadsToBoardData,
} from '@/core/components/leads/leads-board-item';
import { persistLeadBoardUpdates } from '@/core/components/leads/leads-board-persist';
import {
  LEAD_STATUS_IDS,
  leadStatusAccent,
} from '@/core/components/leads/lead-status-colors';
import { useLeadsBoardDnd } from '@/core/components/leads/use-leads-board-dnd';
import { useLeadsBoardSecondFingerScroll } from '@/core/components/leads/use-leads-board-second-finger-scroll';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { leadsQueryKeys } from '@/core/query-keys/leads-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

interface LeadsBoardViewProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
  queryKey: QueryKey;
  leadCardMeta?: (lead: Lead) => LeadCardMetaLines | null | undefined;
  labelMaps?: LeadCardLabelMaps;
  /** Column "+ Lead" — receives that column's status for the new lead. */
  onAddLead?: (status: LeadStatus) => void;
  dropHereLabel?: string;
}

export function LeadsBoardView({
  leads,
  onLeadClick,
  queryKey,
  leadCardMeta,
  labelMaps,
  onAddLead,
  dropHereLabel,
}: LeadsBoardViewProps) {
  const dict = useDictionary();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const sourceLabels = (dict.leads.list.source ?? {}) as Record<string, string>;
  const columnAddLabel =
    (dict.leads.list as { boardAddLead?: string }).boardAddLead ?? 'Lead';
  const resolvedDropHereLabel =
    dropHereLabel ??
    (dict.leads.list as { boardDropHere?: string }).boardDropHere ??
    'Hier ablegen';
  const [boardData, setBoardData] = useState<LeadBoardItem[]>(() =>
    leadsToBoardData(leads),
  );
  const boardDataRef = useRef<LeadBoardItem[]>(leadsToBoardData(leads));
  const dragStartSnapshotRef = useRef<LeadBoardItem[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lockedBoardColumnHeight, setLockedBoardColumnHeight] = useState<
    number | null
  >(null);
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  /** Für DragOverlay: welche Karte gerade gezogen wird (Board + List) */
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const boardScrollRef = useRef<HTMLDivElement>(null);

  useLeadsBoardSecondFingerScroll(isDragging, boardScrollRef);

  const columns = useMemo(
    () =>
      LEAD_STATUS_IDS.map((id) => ({
        id,
        name: dict.leads.status?.[id as keyof typeof dict.leads.status] ?? id,
      })),
    [dict.leads.status],
  );

  const leadsSyncSignature = useMemo(
    () =>
      leads
        .map((l) => `${l.id}:${l.status}:${l.order ?? ''}`)
        .sort()
        .join('|'),
    [leads],
  );
  const [prevLeadsSyncSignature, setPrevLeadsSyncSignature] =
    useState(leadsSyncSignature);
  if (!isDragging && leadsSyncSignature !== prevLeadsSyncSignature) {
    setPrevLeadsSyncSignature(leadsSyncSignature);
    setBoardData(leadsToBoardData(leads));
  }

  useEffect(() => {
    boardDataRef.current = boardData;
  }, [boardData]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root });
    void invalidateStatusBoardQueries(queryClient);
  }, [queryClient, queryKey]);

  const leadsById = useMemo(
    () => new Map<string, Lead>(leads.map((l) => [l.id, l])),
    [leads],
  );

  /** Card counts per column drive the minimap's mini-card visualisation. */
  const minimapColumns = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        color: leadStatusAccent(column.id),
        cardCount: boardData.filter((item) => item.column === column.id).length,
      })),
    [columns, boardData],
  );

  const persistBoard = useCallback(async () => {
    await persistLeadBoardUpdates({
      finalData: boardDataRef.current,
      startSnapshot: dragStartSnapshotRef.current,
      leadsById,
      workspaceId: currentWorkspace?.id,
      statusUpdateFailedMessage:
        dict.leads.detail?.toast?.statusUpdateFailed ??
        'Änderungen konnten nicht gespeichert werden.',
      invalidate,
    });
  }, [
    currentWorkspace?.id,
    dict.leads.detail?.toast?.statusUpdateFailed,
    invalidate,
    leadsById,
  ]);

  const {
    sensors,
    handleDragStart,
    handleDragMove,
    handleBoardDragOver,
    handleBoardDragEnd,
    handleBoardDragCancel,
  } = useLeadsBoardDnd({
    columns,
    boardDataRef,
    dragStartSnapshotRef,
    boardScrollRef,
    setBoardData,
    setIsDragging,
    setLockedBoardColumnHeight,
    setActiveCardId,
    persistBoard,
  });

  if (!mounted) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={kanbanBoardCollisionDetection}
      // Built-in edge-scroll: scrolls the board container when the dragged
      // card approaches the left/right edge. dnd-kit updates droppable
      // positions in sync with the scroll so collision detection stays
      // accurate. Vertical auto-scroll is disabled (y:0) — only the board
      // scrolls horizontally.
      autoScroll={{ threshold: { x: 0.2, y: 0 }, acceleration: 13 }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleBoardDragOver}
      onDragEnd={handleBoardDragEnd}
      onDragCancel={handleBoardDragCancel}
    >
      {/*
       * Bleed out of AppPageLayout padding so the scroll viewport reaches the
       * page edges while columns stay aligned with the rest of the page.
       */}
      <div
        ref={boardScrollRef}
        className="app-page-bleed-x flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto overflow-y-hidden pb-2 pt-0.5 md:snap-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {columns.map((column) => (
          <LeadsBoardColumn
            key={column.id}
            columnId={column.id}
            columnName={column.name}
            data={boardData}
            leadsById={leadsById}
            sourceLabels={sourceLabels}
            addLeadLabel={columnAddLabel}
            onLeadClick={onLeadClick}
            onAddLead={onAddLead ? () => onAddLead(column.id) : undefined}
            isDropTarget={
              activeCardId != null &&
              boardData.some(
                (item) => item.id === activeCardId && item.column === column.id,
              )
            }
            lockedBoardColumnHeight={lockedBoardColumnHeight}
            leadCardMeta={leadCardMeta}
            labelMaps={labelMaps}
            dropHereLabel={resolvedDropHereLabel}
          />
        ))}
      </div>

      <KanbanMinimap
        scrollContainerRef={boardScrollRef}
        columns={minimapColumns}
      />

      {typeof window !== 'undefined' &&
        createPortal(
          <DragOverlay dropAnimation={null} style={{ pointerEvents: 'none' }}>
            {activeCardId
              ? (() => {
                  const lead = leadsById.get(activeCardId);
                  if (!lead) return null;
                  return (
                    <Card className="min-w-[200px] max-w-[248px] cursor-grabbing select-none border-[color:var(--surface-border)] shadow-lg ring-2 ring-primary">
                      <CardContent className="p-3">
                        <LeadBoardCardContent
                          lead={lead}
                          locale={locale}
                          sourceLabels={sourceLabels}
                          meta={leadCardMeta?.(lead) ?? undefined}
                          labelMaps={labelMaps}
                        />
                      </CardContent>
                    </Card>
                  );
                })()
              : null}
          </DragOverlay>,
          document.body,
        )}
    </DndContext>
  );
}
